import React, { useState, useEffect } from 'react';
import { FaArrowUp, FaSearch, FaEllipsisV } from 'react-icons/fa';
import { FiPackage } from 'react-icons/fi';
import { ImPower } from "react-icons/im";
import { SlRefresh } from "react-icons/sl";
import SearchAndSelectList from "../../SearchableList/SearchAndSelectList"; 
import StartDateRangeFilter from "../../SearchableList/StartDateRangeFilter"; 
import DateRangeFilter from "../../SearchableList/DateRangeFilter"; 
import { createPortal } from "react-dom";
import Dialog from "../../Admin/FormDialog/Dialog";
import { OUTAGE_TAB } from '../../Admin/constants/constants';

import { getOutageHeaders } from "../apiHeadersOutage";
import apiClientOutage from '../../../services/apiClientOutage';

type OutageSearchableFilterType =
  | "outageID"
  | "isoID"
  | "asset_resource"
  | "status"
  | "iso"
  | "workflow";

type OutageFilterType = OutageSearchableFilterType | "start_date" | "end_date";

const PortalDropdown: React.FC<{
  children: React.ReactNode;
  position: { top: number; left: number };
}> = ({ children, position }) => {
  return createPortal(
    <div
      className="absolute z-[9999] bg-white shadow-lg border rounded-md"
      style={{
        top: position.top,
        left: position.left,
        minWidth: '200px',
        position: "absolute"
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};




interface OutageTableProps {
  assets: any[];
  outages: any[];
  onEditOutage: (outage: any) => void;
  onRowSelect: (outage: any) => void;
  onClearSelection: () => void;
  selectedRow: string | null; 
  //   onDeleteSelected: () => void;  // Function to delete selected outage
  selectedOutage: any | null;  // Single selected outage
  setSelectedOutage: (outage: any) => void;  // Function to set the selected outage
  fetchAssets: (searchQuery: string) => void; 
  isHistoricalOn: boolean;
  onToggleHistorical: (value: boolean) => void;
  onRefresh: () => Promise<void>;
}

const OutageTable: React.FC<OutageTableProps> = ({
  assets,
  outages,
  onEditOutage,
  onRowSelect,
  onClearSelection,
  selectedRow, 
//   onDeleteSelected,
  selectedOutage,
  setSelectedOutage,
  fetchAssets,
  isHistoricalOn,
  onToggleHistorical,
  onRefresh,

}) => {


    const [dialog, setDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        confirmButtonText: string;
        cancelButtonText?: string;
        onConfirm: () => void;
        onCancel?: () => void;
        copyableContent?: string;
      }>({
        open: false,
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
      });
    
      const closeDialog = () => {
        setDialog({
          open: false,
          title: "",
          message: "",
          confirmButtonText: "",
          onConfirm: () => {},
          copyableContent: undefined,
        });
      };
      
  
  
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [openFilter, setOpenFilter] = useState<OutageFilterType | null>(null);
    const [draftSelection, setDraftSelection] = useState<string[]>([]);
    const [openFilterItems, setOpenFilterItems] = useState<string[]>([]);


    const uniqueisoIDs = Array.from(new Set(outages.map(o => [...(o.comments || [])].reverse().find(c => c.versifyLogid?.length > 5)?.versifyLogid).filter(Boolean)));
    const uniqueStatuses = Array.from(new Set(outages.map(o => o.outage_status_code))).sort();
    const uniqueWorkflows = Array.from(new Set(outages.map(o => o.workflow_type_code))).sort();
    const uniqueAssetResourceNames = Array.from(
        new Set(
            assets.flatMap((asset: { asset_name: string; resources?: { iso_resource_name: string }[] }) => [
                asset.asset_name, 
                ...(asset.resources?.map((resource: { iso_resource_name: string }) => resource.iso_resource_name) || [])
            ])
        )
    );
    const uniqueISO = Array.from(new Set(assets.map(asset => asset.iso_code))).sort(); 

    const getDefaultStartDateRange = () => {
        const startOfCurrentMonth = new Date();
        startOfCurrentMonth.setUTCDate(1); // Set to the 1st day of the month
        startOfCurrentMonth.setUTCHours(0, 0, 0, 0);

        const currentDayPlusOne = new Date();
        currentDayPlusOne.setUTCDate(currentDayPlusOne.getUTCDate() + 1); // Set to current day + 1
        currentDayPlusOne.setUTCHours(23, 59, 59, 999);

        // Format to 'YYYY-MM-DDTHH:MM' for datetime-local compatibility
        const formattedFrom = startOfCurrentMonth.toISOString().slice(0, 16);
        const formattedTo = currentDayPlusOne.toISOString().slice(0, 16);

        return { from: formattedFrom, to: formattedTo };
    };

    const [selectedOutageIDs, setSelectedOutageIDs] = useState<string[]>([]);
    const [selectedisoIDs, setSelectedisoIDs] = useState<string[]>([]);
    const [selectedAssetResourceNames, setSelectedAssetResourceNames] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["Active", "Approved"]); 
    const [selectedISOs, setSelectedISOs] = useState<string[]>([]);
    const [selectedWorkflows, setSelectedWorkflows] = useState<string[]>([]);
    const [startDateRange, setStartDateRange] = useState<{ from: string; to: string }>(getDefaultStartDateRange()); 
    const [endDateRange, setEndDateRange] = useState<{ from: string; to: string }>({ from: "", to: "" });

    const [sortConfig, setSortConfig] = useState({ key: "start_datetime", direction: "desc" });

    const searchableFilterTypes = new Set<OutageSearchableFilterType>([
      "outageID",
      "isoID",
      "asset_resource",
      "status",
      "iso",
      "workflow",
    ]);

    const isSearchableFilter = (
      filterType: OutageFilterType | null
    ): filterType is OutageSearchableFilterType => {
      return !!filterType && searchableFilterTypes.has(filterType as OutageSearchableFilterType);
    };

    const orderSelectedFirst = (items: string[], selectedValues: string[]) => {
      const selectedSet = new Set(selectedValues);
      const selected = items.filter((item) => selectedSet.has(item));
      const unselected = items.filter((item) => !selectedSet.has(item));
      return [...selected, ...unselected];
    };

    const getCommittedFilterSelection = (filterType: OutageSearchableFilterType): string[] => {
      switch (filterType) {
        case "outageID":
          return selectedOutageIDs;
        case "isoID":
          return selectedisoIDs;
        case "asset_resource":
          return selectedAssetResourceNames;
        case "status":
          return selectedStatuses;
        case "iso":
          return selectedISOs;
        case "workflow":
          return selectedWorkflows;
        default:
          return [];
      }
    };

    const setCommittedFilterSelection = (
      filterType: OutageSearchableFilterType,
      values: string[]
    ) => {
      switch (filterType) {
        case "outageID":
          setSelectedOutageIDs(values);
          break;
        case "isoID":
          setSelectedisoIDs(values);
          break;
        case "asset_resource":
          setSelectedAssetResourceNames(values);
          break;
        case "status":
          setSelectedStatuses(values);
          break;
        case "iso":
          setSelectedISOs(values);
          break;
        case "workflow":
          setSelectedWorkflows(values);
          break;
        default:
          break;
      }
    };

    const closeOpenFilter = () => {
      if (isSearchableFilter(openFilter)) {
        setCommittedFilterSelection(openFilter, draftSelection);
      }

      setOpenFilter(null);
      setDraftSelection([]);
      setOpenFilterItems([]);
    };

    const openSearchableFilter = (
      filterType: OutageSearchableFilterType,
      items: string[]
    ) => {
      const committedSelection = getCommittedFilterSelection(filterType);
      setDraftSelection(committedSelection);
      setOpenFilterItems(orderSelectedFirst(items, committedSelection));
      setOpenFilter(filterType);
    };

    const handleEllipsisClick = (e: React.MouseEvent, filterType: OutageFilterType) => {
      e.stopPropagation();
      const thElement = (e.currentTarget as HTMLElement).closest("th");
      if (!thElement) return;

      const rect = thElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });

      if (openFilter === filterType) {
        closeOpenFilter();
        return;
      }

      if (isSearchableFilter(openFilter)) {
        setCommittedFilterSelection(openFilter, draftSelection);
      }

      if (filterType === "isoID") {
        openSearchableFilter("isoID", uniqueisoIDs);
        return;
      }

      if (filterType === "asset_resource") {
        openSearchableFilter("asset_resource", uniqueAssetResourceNames);
        return;
      }

      if (filterType === "status") {
        openSearchableFilter("status", uniqueStatuses);
        return;
      }

      if (filterType === "iso") {
        openSearchableFilter("iso", uniqueISO);
        return;
      }

      if (filterType === "workflow") {
        openSearchableFilter("workflow", uniqueWorkflows);
        return;
      }

      setDraftSelection([]);
      setOpenFilterItems([]);
      setOpenFilter(filterType);
    };


    const applyStartDateFilter = (
        startDate: string,
        endDate: string,
        range: { from: string; to: string }
    ) => {
        if (!range.from && !range.to) return true;

        const start = new Date(startDate);
        const end = new Date(endDate);
        const fromDate = range.from ? new Date(range.from) : null;
        const toDate = range.to ? new Date(range.to) : null;

        return (
            (!fromDate || end >= fromDate) && // End date should be on or after the start filter
            (!toDate || start <= toDate) // Start date should be on or before the end filter
        );
    };
    const applyDateFilter = (date: string, range: { from: string; to: string }) => {
        if (!range.from && !range.to) return true;
        const dateObj = new Date(date);
        const fromDate = range.from ? new Date(range.from) : null;
        const toDate = range.to ? new Date(range.to) : null;

        return (
            (!fromDate || dateObj >= fromDate) &&
            (!toDate || dateObj <= toDate)
        );
    };


    // const getAssetResourceName = (reference_id: number): string | undefined => {
    //     const asset = assets.find(a => a.asset_id === reference_id);
    //     if (asset) return asset.asset_name;
      
    //     const resource = assets.flatMap(a => a.resources || []).find(r => r.resource_id === reference_id);
    //     console.log()
    //     return resource?.iso_resource_name;
    // };
    
    // const getISO = (reference_id: number): string | undefined => {
    //     const asset = assets.find(a => a.asset_id === reference_id);
    //     if (asset) return asset.iso_code;
      
    //     const resource = assets.flatMap(a => a.resources || []).find(r => r.resource_id === reference_id);
    //     return resource?.iso_code;
    // };
    // const getAssetResourceExportName = (reference_id: number): string | undefined => {
    //     const asset = assets.find(a => a.asset_id === reference_id);
    //     if (asset) return asset.asset_name;
    
    //     const resource = assets.flatMap(a => a.resources || []).find(r => r.resource_id === reference_id);
    //     return resource ? `${resource.asset_name}-${resource.iso_resource_name}` : undefined;
    // };
    const getAssetResourceName = (
        reference_id: number,
        reference_type_code: string
      ): string | undefined => {
        if (reference_type_code === "Asset") {
          const asset = assets.find(a => a.asset_id === reference_id);
          return asset?.asset_name;
        } else if (reference_type_code === "Resource") {
          const resource = assets.flatMap(a => a.resources || []).find(r => r.resource_id === reference_id);
          return resource?.iso_resource_name;
        }
        return undefined;
      };
      
      const getISO = (
        reference_id: number,
        reference_type_code: string
      ): string | undefined => {
        if (reference_type_code === "Asset") {
          const asset = assets.find(a => a.asset_id === reference_id);
          return asset?.iso_code;
        } else if (reference_type_code === "Resource") {
          const resource = assets.flatMap(a => a.resources || []).find(r => r.resource_id === reference_id);
          return resource?.iso_code;
        }
        return undefined;
      };
      
      const getAssetResourceExportName = (
        reference_id: number,
        reference_type_code: string
      ): string | undefined => {
        if (reference_type_code === "Asset") {
          const asset = assets.find(a => a.asset_id === reference_id);
          return asset?.asset_name;
        } else if (reference_type_code === "Resource") {
          const resource = assets.flatMap(a => a.resources || []).find(r => r.resource_id === reference_id);
          return resource ? `${resource.asset_name}-${resource.iso_resource_name}` : undefined;
        }
        return undefined;
      };
      

    const filteredOutages = outages.filter(outage =>
        (selectedOutageIDs.length === 0 || selectedOutageIDs.includes(outage.outage_id)) &&
        (selectedisoIDs.length === 0 || selectedisoIDs.includes(
            [...(outage.comments || [])]
              .reverse()
              .find(comment => comment.versifyLogid && comment.versifyLogid.length >= 5)?.versifyLogid || 'N/A'
          )) &&
        (selectedStatuses.length === 0 || selectedStatuses.includes(outage.outage_status_code)) &&
        (selectedAssetResourceNames.length === 0 || (getAssetResourceName(outage.reference_id, outage.reference_type_code) !== undefined && selectedAssetResourceNames.includes(getAssetResourceName(outage.reference_id, outage.reference_type_code)!))) &&
        (selectedISOs.length === 0 || (getISO(outage.reference_id, outage.reference_type_code) !== undefined && selectedISOs.includes(getISO(outage.reference_id, outage.reference_type_code)!))) && 
        (selectedWorkflows.length === 0 || selectedWorkflows.includes(outage.workflow_type_code)) &&
        applyStartDateFilter(outage.start_datetime, outage.end_datetime, startDateRange) &&
        applyDateFilter(outage.end_datetime, endDateRange)
    );


    const sortOutages = (key: string, direction: string) => {
        return [...filteredOutages].sort((a, b) => {
            let valA, valB;
    
            if (key === "asset_resource") {
                valA = getAssetResourceName(a.reference_id, a.reference_type_code) || "";
                valB = getAssetResourceName(b.reference_id, b.reference_type_code) || "";
            } else if (key === "iso") {
                valA = getISO(a.reference_id, a.reference_type_code) || "";
                valB = getISO(b.reference_id, b.reference_type_code) || "";
            // } else if (key === "iso_id") {
            //     valA = a.comments?.[a.comments.length - 1]?.versifyLogid || "";
            //     valB = b.comments?.[b.comments.length - 1]?.versifyLogid || "";
            } else if (key === "iso_id") {
                const getLastValidIsoID = (comments: any[] = []) =>
                    [...comments]
                    .reverse()
                    .find(c => c.versifyLogid && c.versifyLogid.length >= 5)?.versifyLogid || "N/A";
            
                valA = getLastValidIsoID(a.comments);
                valB = getLastValidIsoID(b.comments);
            } else {
                valA = a[key];
                valB = b[key];
    
                if (key.includes("datetime") || key.includes("date")) {
                    valA = new Date(valA).getTime();
                    valB = new Date(valB).getTime();
                } else {
                    valA = valA?.toString().toLowerCase();
                    valB = valB?.toString().toLowerCase();
                }
            }
    
            return direction === "asc" ? (valA < valB ? -1 : 1) : (valA > valB ? -1 : 1);
        });
    };
    const sortedOutages = sortOutages(sortConfig.key, sortConfig.direction);
    const handleSort = (key: string) => {
        const newDirection = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction: newDirection });
      };
      
  
    const handleSelectRow = (outage: any) => {
        onRowSelect(outage); 
    };


    const handleClearFilters = () => {
        setSelectedOutageIDs([]);
        setSelectedisoIDs([]);
        setSelectedAssetResourceNames([]);
        setSelectedStatuses(["Active", "Approved"]);
        setSelectedISOs([]);
        setSelectedWorkflows([]);
        setStartDateRange(getDefaultStartDateRange());
        setEndDateRange({ from: "", to: "" });
        setSortConfig({ key: "start_datetime", direction: "desc" });

        // ✅ If in historical mode, switch it OFF
        if (isHistoricalOn) {
          onToggleHistorical(false);
        }
    };
    
    

    const formatLocalDateTime = (datetimeStr: string) => {
        const date = new Date(datetimeStr);
        const month = date.getMonth() + 1; // Months are 0-indexed
        const day = date.getDate();
        const year = date.getFullYear();
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${month}/${day}/${year} ${hours}:${minutes}`;
      };
    const handleExportCSV = () => {
        const today = new Date().toISOString().split("T")[0]; // Get YYYY-MM-DD format
        const fileName = `outage_data_${today}.csv`;
    
        const headers = [
            "Outage ID", 
            "Reference ID", 
            "Reference Type", 
            "Asset/Resource Name", 
            "ISO", 
            "Start DateTime(EST)", 
            "End DateTime(EST)", 
            "Outage Status", 
            "Workflow Type", 
            "Created By", 
            "Modified By", 
            "Created Date(EST)", 
            "Modified Date(EST)", 
            "Comments"
        ];
    
        const csvRows = sortedOutages.map(outage => [
            outage.outage_id,
            outage.reference_id,
            outage.reference_type_code,
            getAssetResourceExportName(outage.reference_id, outage.reference_type_code) || outage.reference_type_code, // Asset/Resource Name
            getISO(outage.reference_id, outage.reference_type_code) || outage.reference_type_code, // ISO
            formatLocalDateTime(outage.start_datetime),
            formatLocalDateTime(outage.end_datetime),
            outage.outage_status_code,
            outage.workflow_type_code,
            outage.sys_created_by,
            outage.sys_modified_by,
            formatLocalDateTime(outage.sys_created_date),
            formatLocalDateTime(outage.sys_modified_date),
            // outage.comments ? outage.comments.map(comment => `${comment.versifyUpdateDate} - ${comment.updateUser}: ${comment.logEntry}`).join(" | ") : "No Comments"
            (Array.isArray(outage.comments) && outage.comments.length > 0)
            ? outage.comments.map((comment: { versifyUpdateDate: string; updateUser: string; logEntry: string }) => 
                `${comment.versifyUpdateDate} - ${comment.updateUser}: ${comment.logEntry}`
              ).join(" | ")
            : "No Comments"
                ]);
    
        const csvContent = [
            headers.join(","), 
            ...csvRows.map(row => row.map(value => `"${value}"`).join(","))
        ].join("\n");
    
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName; // Set dynamic file name
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleExportExcel = async () => {
        try {
          const today = new Date().toISOString().split("T")[0];
          const csvFileName = `outage_data_${today}.csv`;
      
          // 1️⃣ Build CSV
          const headersRow = [
            "Outage ID",
            "Reference ID",
            "Reference Type",
            "Asset/Resource Name",
            "ISO",
            "Start DateTime(EST)",
            "End DateTime(EST)",
            "Outage Status",
            "Workflow Type",
            "Created By",
            "Modified By",
            "Created Date(EST)",
            "Modified Date(EST)",
            "Comments"
          ];
      
          const csvRows = sortedOutages.map(outage => [
            outage.outage_id,
            outage.reference_id,
            outage.reference_type_code,
            getAssetResourceExportName(outage.reference_id, outage.reference_type_code) || outage.reference_type_code,
            getISO(outage.reference_id, outage.reference_type_code) || outage.reference_type_code,
            formatLocalDateTime(outage.start_datetime),
            formatLocalDateTime(outage.end_datetime),
            outage.outage_status_code,
            outage.workflow_type_code,
            outage.sys_created_by,
            outage.sys_modified_by,
            formatLocalDateTime(outage.sys_created_date),
            formatLocalDateTime(outage.sys_modified_date),
            Array.isArray(outage.comments) && outage.comments.length > 0
              ? outage.comments
                  .map((c: any) =>
                    `${c.versifyUpdateDate} - ${c.updateUser}: ${c.logEntry}`
                  )
                  .join(" | ")
              : "No Comments"
          ]);
      
          const csvContent = [
            headersRow.join(","),
            ...csvRows.map(row => row.map(val => `"${val ?? ""}"`).join(","))
          ].join("\n");
      
          const csvBlob = new Blob([csvContent], { type: "text/csv" });
      
          const formData = new FormData();
          formData.append("file", csvBlob, csvFileName);
      
          // 2️⃣ Get outage headers
          const requestHeaders = await getOutageHeaders();
      
          // ⚠️ Remove Content-Type so browser sets multipart boundary
        //   if (requestHeaders["Content-Type"]) {
        //     delete requestHeaders["Content-Type"];
        //   }
      
          // 3️⃣ Send request using fetch
          const response = await fetch(
            `${process.env.REACT_APP_OUTAGES_API}/outage-metadata/translate-to-xlsx`,
            {
              method: "POST",
              headers: requestHeaders,
              body: formData
            }
          );
      
          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }
      
          // 4️⃣ Get Excel blob
          const excelBlob = await response.blob();
      
          // 5️⃣ Extract filename from response headers
          const contentDisposition = response.headers.get("content-disposition");
          let fileName = `outage_data_${today}.xlsx`;
      
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?(.+)"?/);
            if (match?.[1]) {
              fileName = match[1];
            }
          }
      
          // 6️⃣ Trigger download
          const url = window.URL.createObjectURL(excelBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
      
        } catch (error) {
          console.error("Excel export failed:", error);
          alert("Failed to export Excel file.");
        }
      };


      const handleTurnoverMsg = async () => {
        try {
          const requestHeaders = await getOutageHeaders();
      
          const response = await apiClientOutage.get(
            "/outage-metadata/turnover-msg",
            {
              headers: requestHeaders,
              validateStatus: () => true // 🔥 allow handling 404 manually
            }
          );
      
          // ✅ 200 → Upcoming outages exist
          if (response.status === 200 && response.data?.response) {
            const msg = response.data.response?.data || response.data.response?.message || "Upcoming outages found.";
            setDialog({
              open: true,
              title: "Upcoming Outages",
              message: msg,
              confirmButtonText: "Close",
              onConfirm: closeDialog,
              copyableContent: typeof msg === "string" ? msg : undefined
            });
          }
      
          // ❌ 404 → No upcoming outages
          else if (response.status === 404 && response.data?.error?.error?.message) {
            setDialog({
              open: true,
              title: "No Upcoming Outages",
              message: response.data.error.error.message,
              confirmButtonText: "Close",
              onConfirm: closeDialog
            });
          }
      
          // ⚠️ Unexpected case
          else {
            setDialog({
              open: true,
              title: "Unexpected Response",
              message: "Unexpected server response.",
              confirmButtonText: "Close",
              onConfirm: closeDialog
            });
          }
      
        } catch (error) {
          console.error("Turnover message fetch failed:", error);
      
          setDialog({
            open: true,
            title: "Error",
            message: "Failed to fetch turnover message.",
            confirmButtonText: "Close",
            onConfirm: closeDialog
          });
        }
      };
      
      


    const handleButtonClick = (type: string) => {
        setSelectedButton(type);
    
        if (type === 'active') {
            setSelectedOutageIDs([]);
            setSelectedStatuses(["Active"]);
            setStartDateRange({ from: "", to: "" });
            setEndDateRange({ from: "", to: "" });
        }
    
        if (type === 'notActivated') {
            setSelectedOutageIDs([]);
            setSelectedStatuses(["New", "Approved"]);
    
            const currentDate = new Date().toISOString();
            setStartDateRange({ from: "1970-01-01T00:00:00Z", to: currentDate });
            setEndDateRange({ from: currentDate, to: "9999-12-31T23:59:59Z" });
        }
    
        if (type === 'notCompleted') {
            setSelectedOutageIDs([]);
            setSelectedStatuses(["New", "Approved", "Active"]);
    
            const currentDate = new Date().toISOString();
            setEndDateRange({ from: "1970-01-01T00:00:00Z", to: currentDate });
        }

        if (type === 'upcoming') {
            setSelectedStatuses([]);
            setStartDateRange({ from: "", to: "" });
            setEndDateRange({ from: "", to: "" });
            const currentDate = new Date().getTime();
            const thirteenHours = 13 * 60 * 60 * 1000;
        
            // Filter matching outages
            const matching = outages.filter((item) => {
                const start = new Date(item.start_datetime).getTime();
                const end = new Date(item.end_datetime).getTime();
        
                return (
                    (item.outage_status_code === "Approved" && start - currentDate <= thirteenHours && start - currentDate >= 0) ||
                    (item.outage_status_code === "Active" && end - currentDate <= thirteenHours && end - currentDate >= 0)
                );
            });
        
            // Store IDs for table filtering
            const matchingIds = matching.map((item) => item.outage_id);
            setSelectedOutageIDs(matchingIds);
        
            // Format information for copy
            const textToCopy = matching.map((item) => {
                const start = new Date(item.start_datetime).getTime();
                const end = new Date(item.end_datetime).getTime();
        
                return `Asset/Resource: ${getAssetResourceName(item.reference_id, item.reference_type_code)} | ISO: ${getISO(item.reference_id, item.reference_type_code)} | ${
                    start - currentDate <= thirteenHours && start - currentDate >= 0 ? "Starts" : "Ends" } | Time(EST): ${start - currentDate <= thirteenHours && start - currentDate >= 0 ? new Date(item.start_datetime).toLocaleString() : new Date(item.end_datetime).toLocaleString() }`;
            }).join('\n');
        
            navigator.clipboard.writeText(textToCopy)
                .then(() => {
                    setDialog({
                        open: true,
                        title: OUTAGE_TAB.UPCOMING.UPCOMING_INFO_TITLE,
                        message: OUTAGE_TAB.UPCOMING.UPCOMING_INFO_MESSAGE,
                        confirmButtonText: "Close",
                        onConfirm: closeDialog,
                      });
                })
                .catch((err) => {
                    console.error('Failed to copy!', err);
                });
            }
        };
    const [activeRowCount, setActiveRowCount] = useState<number>(0);
    const [betweenDatesRowCount, setBetweenDatesRowCount] = useState<number>(0);
    const [pastEndDateRowCount, setPastEndDateRowCount] = useState<number>(0);
    const [upcomingRowCount, setUpcomingRowCount] = useState<number>(0);
    const [selectedButton, setSelectedButton] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [justRefreshed, setJustRefreshed] = useState(false);

    useEffect(() => {
        // Calculate counts for different criteria
        const calculateCounts = () => {
            // Active rows count
            const activeCount = outages.filter((item: { outage_status_code: string }) => item.outage_status_code === "Active").length;
            setActiveRowCount(activeCount);
    
            // Rows where current datetime is between start and end dates and status is not "Active"
            const betweenDatesCount = outages.filter((item: { start_datetime: string; end_datetime: string; outage_status_code: string }) => {
                const currentDate = new Date();
                return (new Date(item.start_datetime) <= currentDate && currentDate <= new Date(item.end_datetime) && (item.outage_status_code === "New" || item.outage_status_code === "Approved"));
            }).length;
            setBetweenDatesRowCount(betweenDatesCount);
    
            // Rows where current datetime is greater than end date and status is not "Completed"
            const pastEndDateCount = outages.filter((item: { end_datetime: string; outage_status_code: string }) => {
                const currentDate = new Date();
                return (new Date(item.end_datetime) < currentDate && (item.outage_status_code === "New" || item.outage_status_code === "Approved" || item.outage_status_code === "Active"));
            }).length;
            setPastEndDateRowCount(pastEndDateCount);
        };
    
        calculateCounts();
    }, [outages]);
    useEffect(() => {
        // Calculate number of matching (upcoming) outages
        const currentDate = new Date().getTime();
        const thirteenHours = 13 * 60 * 60 * 1000;
    
        const matching = outages.filter((item) => {
            const start = new Date(item.start_datetime).getTime();
            const end = new Date(item.end_datetime).getTime();
    
            return (
                (item.outage_status_code === "Approved" && start - currentDate <= thirteenHours && start - currentDate >= 0) ||
                (item.outage_status_code === "Active" && end - currentDate <= thirteenHours && end - currentDate >= 0)
            );
        });
    
        setUpcomingRowCount(matching.length);
    }, [outages]);

  return (

<div className="flex-1 bg-white p-5 rounded-xl font-poppins relative overflow-hidden h-full flex flex-col">
    {dialog.open && (
    <Dialog
        title={dialog.title}
        message={dialog.message}
        confirmButtonText={dialog.confirmButtonText}
        cancelButtonText={dialog.cancelButtonText}
        onConfirm={dialog.onConfirm}
        onCancel={dialog.onCancel}
        onClose={closeDialog}
        copyableContent={dialog.copyableContent}
    />
    )}
        
  {/* Button Container Section */}
  <div className="grid grid-cols-1 sm:grid-cols-4 justify-items-center gap-4 mb-6 w-full">
    {/* Active button */}
    <button 
        className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg shadow text-white text-sm min-w-[150px]"
        style={{ backgroundColor: activeRowCount > 0 ? 'green' : 'lightgrey' }}
        onClick={() => handleButtonClick('active')}
    >
        <span className="text-lg font-bold">{activeRowCount}</span>
        <span>ACTIVE</span>
    </button>

    {/* Not Activated button */}
    <button 
        className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg shadow text-white text-sm min-w-[150px]"
        style={{ backgroundColor: betweenDatesRowCount > 0 ? 'orange' : 'lightgrey' }}
        onClick={() => handleButtonClick('notActivated')}
    >
        <span className="text-lg font-bold">{betweenDatesRowCount}</span>
        <span>NOT ACTIVATED</span>
    </button>

    {/* Not Completed button */}
    <button 
        className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg shadow text-white text-sm min-w-[150px]"
        style={{ backgroundColor: pastEndDateRowCount > 0 ? 'red' : 'lightgrey' }}
        onClick={() => handleButtonClick('notCompleted')}
    >
        <span className="text-lg font-bold">{pastEndDateRowCount}</span>
        <span>NOT COMPLETED</span>
    </button>

    {/* Turnover button */}
<button
    className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg shadow text-white text-sm min-w-[150px] bg-blue-600 hover:bg-blue-500 transition-all"
    onClick={handleTurnoverMsg}
>
    <span>TURNOVER</span>
</button>
  </div>


  {/* Header Section */}
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-xl font-semibold text-gray-800">LOG BOOK</h2>
    <div className="flex items-center gap-3">
    <button
  className="p-2 text-gray-400 rounded-full hover:text-blue-600 transition-all"
  title="Refresh outages"
  disabled={isRefreshing}
  onClick={async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      setJustRefreshed(true);
      setTimeout(() => setJustRefreshed(false), 800);
    } finally {
      setIsRefreshing(false);
    }
  }}
>
  <SlRefresh className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
</button>
    <button
  className={`px-4 py-2 text-sm rounded-lg shadow transition-all ${
    isHistoricalOn
      ? "bg-blue-900 text-white hover:bg-blue-800"   // Same as Reset button color
      : "bg-gray-400 text-white hover:bg-gray-500"
  }`}
  onClick={() => {
    setDialog({
      open: true,
      title: isHistoricalOn
        ? "Turn OFF Full History?"
        : "Turn ON Full History?",
      message: isHistoricalOn
        ? "Turning OFF Full History will resume Live Data mode. Do you want to continue?"
        : "Turning ON Full History will load the complete outage history and LIVE DATA auto-refresh will be turned OFF. Do you want to continue?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        onToggleHistorical(!isHistoricalOn);
        closeDialog();
      },
      onCancel: closeDialog
    });
  }}
>
  Full History
</button>
      <button
        className="px-4 py-2 bg-blue-900 text-white text-sm rounded-lg shadow hover:bg-blue-800 transition-all"
        onClick={handleClearFilters}
      >
        Reset
      </button>
      {/* <button
        className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg shadow hover:bg-green-500 transition-all"
        onClick={handleExportCSV}
      >
        Export CSV
      </button> */}
      <button
  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg shadow hover:bg-green-500 transition-all"
  onClick={handleExportExcel}
>
  Export Excel
</button>

    </div>
  </div>


  {/* Table Section */}
  {/* <div className="relative overflow-hidden z-0 h-full pb-6">
  <div className="relative overflow-auto h-full"> */}
  <div className={`flex flex-col flex-1 min-h-0 rounded-xl transition-all duration-300 ${isRefreshing ? 'opacity-40' : 'opacity-100'} ${justRefreshed ? 'ring-2 ring-green-400' : ''}`}>
  <div className="flex-1 overflow-auto min-h-0">

  <div className="min-w-full">

    <table className="w-full table-auto border-separate border-spacing-0">
      <thead className="bg-[#E7E4FF] sticky top-0 z-30 overflow-visible">
        <tr>

            {/* ISO ID Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer rounded-tl-xl truncate"
            onClick={() => handleSort('iso_id')}
            >
            ISO ID
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'iso_id' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "isoID")}
            />
            {openFilter === "isoID" && (
                <PortalDropdown position={dropdownPosition}>
                <SearchAndSelectList 
                items={openFilterItems}
                selectedItems={draftSelection}
                onSelectionChange={setDraftSelection}
                onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>

            {/* Asset/Resource Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer truncate"
            onClick={() => handleSort('asset_resource')}
            >
            Asset/Resource
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'asset_resource' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "asset_resource")}
            />
            {openFilter === "asset_resource" && (
                <PortalDropdown position={dropdownPosition}>
                <SearchAndSelectList 
                items={openFilterItems}
                selectedItems={draftSelection}
                onSelectionChange={setDraftSelection}
                onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>

            {/* Status Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer truncate"
            onClick={() => handleSort('outage_status_code')}
            >
            Status
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'outage_status_code' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "status")}
            />
            {openFilter === "status" && (
                <PortalDropdown position={dropdownPosition}>
                <SearchAndSelectList 
                items={openFilterItems}
                selectedItems={draftSelection}
                onSelectionChange={setDraftSelection}
                onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>

            {/* ISO Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer truncate"
            onClick={() => handleSort('iso')}
            >
            ISO
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'iso' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "iso")}
            />
            {openFilter === "iso" && (
                <PortalDropdown position={dropdownPosition}>
                <SearchAndSelectList 
                items={openFilterItems}
                selectedItems={draftSelection}
                onSelectionChange={setDraftSelection}
                onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>

            {/* Workflow Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer truncate"
            onClick={() => handleSort('workflow_type_code')}
            >
            Workflow
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'workflow_type_code' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "workflow")}
            />
            {openFilter === "workflow" && (
                <PortalDropdown position={dropdownPosition}>
                <SearchAndSelectList 
                items={openFilterItems}
                selectedItems={draftSelection}
                onSelectionChange={setDraftSelection}
                onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>

            {/* Start DT Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer truncate text-right"
            onClick={() => handleSort('start_datetime')}
            >
            Start DT
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'start_datetime' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "start_date")}
            />
            {openFilter === "start_date" && (
                <PortalDropdown position={dropdownPosition}>
                <StartDateRangeFilter 
                    initialFrom={startDateRange.from} 
                    initialTo={startDateRange.to} 
                    onChange={(from, to) => setStartDateRange({ from, to })} 
                  onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>

            {/* End DT Column */}
            <th
            className="p-4 text-left text-base font-bold text-gray-700 cursor-pointer rounded-tr-xl truncate text-right"
            onClick={() => handleSort('end_datetime')}
            >
            End DT
            <FaArrowUp
                className={`inline-block ml-2 text-gray-600 transition-transform ${
                sortConfig?.key === 'end_datetime' && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                }`}
            />
            <FaEllipsisV
                className="inline-block ml-2 text-gray-600 transition-transform"
                onClick={(e) => handleEllipsisClick(e, "end_date")}
            />
            {openFilter === "end_date" && (
                <PortalDropdown position={dropdownPosition}>
                <DateRangeFilter 
                    initialFrom={endDateRange.from} 
                    initialTo={endDateRange.to} 
                    onChange={(from, to) => setEndDateRange({ from, to })} 
                  onClose={closeOpenFilter}
                />
                </PortalDropdown>
            )}
            </th>


            </tr>
          </thead>
          <tbody>
            {sortedOutages.map((outage) => {
                const now = new Date();
                const startDateTime = new Date(outage.start_datetime);
                const endDateTime = new Date(outage.end_datetime);
                const timeDiffStart = (startDateTime.getTime() - now.getTime()) / (1000 * 60); // in minutes
                const timeDiffEnd = (endDateTime.getTime() - now.getTime()) / (1000 * 60);     // in minutes
                const isStartSoonAndApproved =
                timeDiffStart >= 0 &&
                timeDiffStart <= 30 &&
                outage.outage_status_code === "Approved";

                const isEndSoon = timeDiffEnd >= 0 && timeDiffEnd <= 30;
                const showRedBackground = isStartSoonAndApproved || isEndSoon;

                return (
                <tr
                    key={outage.outage_id}
                    onClick={() => handleSelectRow(outage)}
                    className={`cursor-pointer hover:bg-gray-200 transition-all duration-300 ease-in-out ${
                    outage.outage_id === selectedOutage?.outage_id
                        ? 'text-black font-bold'
                        : ''
                    }`}
                    style={{
                    backgroundColor:
                        outage.outage_id === selectedOutage?.outage_id
                        ? '#fff9c4'
                        : showRedBackground
                        ? 'rgba(255, 0, 0, 0.08)'
                        : 'white',
                    }}
                >
                    <td className="p-4 border-b border-gray-300">
                    {(() => {
                        const validIsoID = [...(outage.comments || [])]
                        .reverse()
                        .find(comment => comment.versifyLogid && comment.versifyLogid.length >= 5);
                        return validIsoID?.versifyLogid || 'N/A';
                    })()}
                    </td>
                    <td className="p-4 border-b border-gray-300">
                    {getAssetResourceName(outage.reference_id, outage.reference_type_code) || outage.reference_type_code}
                    </td>
                    <td
                    className={`p-4 border-b border-gray-300 ${
                        outage.outage_status_code === "Approved"
                        ? "bg-blue-100 text-blue-900"
                        : outage.outage_status_code === "Active"
                        ? "bg-green-100 text-green-900"
                        : outage.outage_status_code === "Completed"
                        ? "bg-gray-200 text-gray-700"
                        : ""
                    }`}
                    >
                    {outage.outage_status_code}
                    </td>
                    <td className="p-4 border-b border-gray-300">
                    {getISO(outage.reference_id, outage.reference_type_code) || outage.reference_type_code}
                    </td>
                    <td className="p-4 border-b border-gray-300">{outage.workflow_type_code}</td>
                    <td className="p-4 border-b border-gray-300 text-right whitespace-nowrap">
                    {new Date(outage.start_datetime).toLocaleString(undefined, { hour12: false })}
                    </td>
                    <td className="p-4 border-b border-gray-300 text-right whitespace-nowrap">
                    {new Date(outage.end_datetime).toLocaleString(undefined, { hour12: false })}
                    </td>
                </tr>
                );
            })}
          </tbody>
        </table>
  </div>
  </div>
  </div>
</div>
  );
};

export default OutageTable;
